import { isPlatformServer } from '@angular/common';
import { inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { SettingDto, SettingService } from '@api-client';

/**
 * 회사 정보 인터페이스
 */
export interface CompanyInfo {
  /** 회사명 */
  name: string;
  /** 주소 */
  address: string;
  /** 전화번호 */
  tel: string;
  /** 이메일 */
  email: string;
  /** 사업자 등록번호 */
  registrationNumber: string;
  /** 대표자명 */
  ceo: string;
  /** 로고 URL */
  logo: string;
  /** 통신판매업 신고번호 */
  commerceNumber: string;
  /** 저작권 문구 */
  copyright: string;
}

/**
 * 설정값 스토어
 * @description 앱 전역에서 사용되는 설정값을 관리하는 스토어
 * @author 정정용 <jeongyong@lepisode.team>
 */
@Injectable({ providedIn: 'root' })
export class SettingStore {
  private readonly settingService = inject(SettingService);
  private readonly platformId = inject(PLATFORM_ID);

  /** 설정값 로딩 상태 */
  readonly isLoading = signal<boolean>(false);

  /** 설정값 로딩 완료 여부 */
  readonly isLoaded = signal<boolean>(false);

  /** 원본 설정값 목록 */
  readonly settings = signal<SettingDto[]>([]);

  /** 회사 정보 */
  readonly companyInfo = signal<CompanyInfo>({
    name: '',
    address: '',
    tel: '',
    email: '',
    registrationNumber: '',
    ceo: '',
    logo: '',
    commerceNumber: '',
    copyright: '',
  });

  constructor() {
    this.fetch();
  }

  /**
   * 설정값 조회
   */
  fetch(): void {
    if (isPlatformServer(this.platformId)) return;
    if (this.isLoaded() || this.isLoading()) return;

    this.isLoading.set(true);

    // 일반 설정과 사업자 정보를 함께 조회
    this.settingService.settingControllerGetAll().subscribe({
      next: (settings: SettingDto[]) => {
        this.settings.set(settings);
        this.parseCompanyInfo(settings);

        // 상세 사업자 정보 추가 조회
        this.settingService.settingControllerGetBusinessInfo().subscribe({
          next: (businessInfo: Record<string, string>) => {
            if (!businessInfo) {
              this.isLoaded.set(true);
              return;
            }

            this.companyInfo.update(info => {
              const baseAddress =
                businessInfo['pigbank_address'] || info.address;
              const addressDetail =
                businessInfo['pigbank_address_detail'] || '';
              const fullAddress = addressDetail
                ? `${baseAddress} ${addressDetail}`
                : baseAddress;

              return {
                ...info,
                commerceNumber: businessInfo['pigbank_commerce_number'] || '',
                ceo: businessInfo['pigbank_ceo_name'] || info.ceo,
                address: fullAddress,
                tel: businessInfo['pigbank_contact'] || info.tel,
                registrationNumber:
                  businessInfo['pigbank_business_number'] ||
                  info.registrationNumber,
                copyright: businessInfo['pigbank_copyright'] || info.copyright,
              };
            });
            this.isLoaded.set(true);
          },
          error: () => {
            this.isLoaded.set(true);
          },
        });
      },
      complete: () => {
        this.isLoading.set(false);
      },
    });
  }

  /**
   * 특정 키의 설정값 조회
   * @param key 설정 키
   * @returns 설정값 또는 빈 문자열
   */
  getValue(key: string): string {
    return this.settings().find(s => s.key === key)?.value || '';
  }

  /**
   * 회사 정보 파싱
   * @param settings 설정값 목록
   */
  private parseCompanyInfo(settings: SettingDto[]): void {
    const info: CompanyInfo = {
      name: settings.find(s => s.key === 'COMPANY:NAME')?.value || '',
      address: settings.find(s => s.key === 'COMPANY:ADDRESS')?.value || '',
      tel: settings.find(s => s.key === 'COMPANY:TEL')?.value || '',
      email: settings.find(s => s.key === 'COMPANY:EMAIL')?.value || '',
      registrationNumber:
        settings.find(s => s.key === 'COMPANY:REGISTRATION_NUMBER')?.value ||
        '',
      ceo: settings.find(s => s.key === 'COMPANY:CEO')?.value || '',
      logo: settings.find(s => s.key === 'COMPANY:LOGO')?.value || '',
      commerceNumber: '',
      copyright: '',
    };
    this.companyInfo.set(info);
  }
}
